import { useEffect, useId, type ReactNode } from 'react';
import FocusTrap from 'focus-trap-react';
import { X } from 'lucide-react';
import { Button } from './Button.js';
import { cn } from '../lib/utils.js';
import { focusRing } from '../lib/focus.js';

/* ===========================================================================
 * WIDTH IS A ROLE, NOT A CONSTANT.
 *
 * Every dialog in the app used to be 480px, so a two-line "Delete this?" and a
 * six-field composer were handed the same box. The confirm looked adrift in it
 * and the composer — a title, a six-row body, two selects, a datetime and a
 * checkbox — read as cramped and narrow, which is exactly the complaint.
 *
 * Three roles, each a token rather than a number:
 *
 *   sm   24rem / 384px   confirms and short prompts — one sentence, two buttons
 *   md   `--dialog-max`  the 480px default: a single-column short form
 *   lg   `--form-max`    a multi-field composer
 *
 * `lg` deliberately reuses `--form-max` — the width ROLE the preset already
 * documents as "a screen whose body is a form". A composer in a dialog is the
 * same content at the same measure, so it gets the same width, and the dialog
 * ends up exactly as wide as the form column on the page it opened over. A
 * fourth private number here would be the drift this token layer exists to stop.
 *
 * `sm` is the one step with no token behind it yet; `max-w-sm` is stock
 * Tailwind's 24rem. See the note on `DIALOG_MAX` below.
 *
 * ALL THREE ARE `sm:`-PREFIXED. Below the `sm` breakpoint the sheet is
 * FULL-BLEED and must stay that way — see the comment on the overlay. A cap
 * that applied at every width would put the phone gutters back.
 * ======================================================================== */
export type DialogSize = 'sm' | 'md' | 'lg';

/**
 * The cap per size role. Written as a literal map, not composed at runtime,
 * because Tailwind scans SOURCE TEXT: a class built as `` `sm:max-w-${size}` ``
 * would compile to nothing and every dialog would silently lose its width.
 *
 * `sm` would ideally read `sm:max-w-dialog-sm` off a `--dialog-sm-max: 24rem`
 * token next to `--dialog-max`. Stock `max-w-sm` is the same 24rem today.
 */
const DIALOG_MAX: Record<DialogSize, string> = {
  sm: 'sm:max-w-sm',
  md: 'sm:max-w-dialog',
  lg: 'sm:max-w-form',
};

/**
 * One verb in a dialog's action row.
 *
 * `form` rather than `onClick` is how a composer submits: the button lives in
 * the footer, outside the scrolling body, and `form="…"` is what lets a submit
 * button sit outside the `<form>` it submits. Native submit also means Enter in
 * any field does what the button does, which a click handler does not give you.
 */
export interface DialogAction {
  /** The verb, as a word. */
  label: string;
  onClick?: () => void;
  /** Submits the `<form id="…">` in the body. */
  form?: string;
  /** `destructive` draws it in danger ink. Default is the filled primary. */
  tone?: 'default' | 'danger'; // Button's own words; was `primary | destructive` (§5)
  disabled?: boolean;
  /**
   * In flight. Disables the row and, with `busyLabel`, says so on the button —
   * a control that looks pressable during a save is one people press twice.
   */
  busy?: boolean;
  busyLabel?: string;
  /**
   * WHY this action cannot be taken, as a sentence. Setting it disables the
   * button AND prints the reason above the row.
   *
   * There is no bare `disabled` for a permanent obstacle, for the same reason
   * `RowActions` has none: §3.11 calls a dead control that does not say why the
   * worst state in the product, and the footer of a dialog somebody opened on
   * purpose is a costly place to hide one. (`disabled` remains, for the
   * ordinary case where the form is simply not filled in yet — that obstacle is
   * visible in the fields above.)
   */
  blockedBy?: string;
  /**
   * Take focus when the dialog opens.
   *
   * Off by default. It is right on a confirmation whose answer is one press,
   * and wrong on anything the reader has to fill in first — focus on a button
   * they cannot yet press is focus taken away from the field they need.
   */
  autoFocus?: boolean;
}

/** The safe half of a choice. Always `ghost`; the caller does not choose. */
export interface DialogDismiss {
  label: string;
  onClick: () => void;
  disabled?: boolean;
}

interface DialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children?: ReactNode;
  /**
   * The confirming action — the one thing this dialog exists to do.
   *
   * STRUCTURED, NOT A `footer` SLOT, and that swap is the whole point. The slot
   * was documented with the right rule ("one full-width primary on a form
   * dialog; a confirmation keeps a weighted pair") and nineteen call sites
   * arrived at nine different readings of it: eight single-button footers said
   * `w-full` and three did not, so the same control was full-bleed on one
   * composer and content-width on the next; five Cancels were `outline` and two
   * `ghost`; three footers nested a second `flex justify-end gap-2` inside this
   * one, which is where the widths actually diverged. None of that was a
   * decision anybody took. It is what a free-form slot produces, because a
   * comment has no failure mode.
   *
   * There is now no way to spell those differences. The geometry lives here and
   * the caller supplies verbs.
   */
  confirm?: DialogAction;
  /**
   * The refusing half of a CHOICE. Omit it on a form dialog.
   *
   * A form dialog already offers three ways out — the ✕, Escape and the
   * backdrop — so a Cancel there is a fourth control doing what three
   * affordances already do, and it takes half the footer to do it.
   *
   * A CONFIRMATION IS THE EXCEPTION and it is not a small one: where the two
   * buttons ARE the question ("Cancel" beside "Terminate", "Revoke", "Delete"),
   * dropping the safe half leaves the destructive answer as the only answer, on
   * the screen where that is most expensive. So the rule is about REDUNDANCY
   * rather than button counts — remove the control that repeats an existing
   * exit, keep the one that is a choice — and it is weighted: `ghost` for the
   * safe half, because an outlined Cancel beside a filled red Delete is two
   * controls of near-equal weight for two decisions of very unequal
   * consequence.
   */
  dismiss?: DialogDismiss;
  /**
   * DESTROY OR DEACTIVATE, and this is the only place either belongs.
   *
   * The rule: a list is for finding and comparing, so the control that ends a
   * record never lives on its row — not visibly, and not behind the row's ⋯
   * either. It lives on the single-item surface, which for most of this product
   * is the edit dialog the row already opens. Going through that surface is the
   * point: you have to be looking at the one thing you are about to end, with
   * its name in the title, before the control is reachable at all.
   *
   * DRAWN AS A GHOST IN DANGER INK, not a filled red button, and placed on the
   * OPPOSITE end of the row from the primary. One weighted button per surface
   * still holds — the weighted one here is Save. A second filled button would
   * make an editing dialog ask two questions of equal loudness, one of which is
   * irreversible.
   *
   * It opens a confirmation rather than acting, and `blockedBy` is how it says
   * why it cannot (a department with staff, a shift still in use).
   */
  danger?: DialogAction;
  /** Optional label for the close button (i18n). Defaults to "Close". */
  /**
   * The close ×'s accessible name — REQUIRED, and it used to fall back to a
   * hard-coded English 'Close'. Twenty-seven of the thirty-nine dialogs in the
   * app took that fallback, so an Arabic reader was told "Close" in English on
   * the one control that has no other name. A default in a primitive that
   * cannot reach i18n is a default nobody can see is wrong.
   */
  closeLabel: string;
  /**
   * Width role — see `DIALOG_MAX`. Defaults to `md`, the 480px every dialog
   * had before this prop existed, so an existing caller only moves when it
   * asks to.
   */
  size?: DialogSize;
  /** Extra panel classes. The width comes from `size`; to override it here you
   *  need the same `sm:` prefix (`sm:max-w-3xl`) — a bare `max-w-*` sits in a
   *  different tailwind-merge group and so lands *alongside* the cap rather
   *  than replacing it. */
  className?: string;
}

/**
 * Accessible modal dialog, shaped as a bottom sheet — at every width.
 *
 * It used to be two shapes: a sheet on a phone, a centred card from `sm` up.
 * That split is gone. This app is meant to read as one product with the
 * sister app, whose sheet is bottom-anchored on every screen, and the anchor
 * turned out to be most of what identifies it — the same panel centred still
 * read as a different component after its borders and padding matched.
 *
 * So: bottom edge, top corners only, 24px padding, grab handle, and a
 * full-height slide up. The cap is the one thing that is NOT fixed — see
 * `size` and the width-is-a-role note above.
 *
 * The body SCROLLS INSIDE the panel while the header and footer stay put. It
 * previously did not, so a form with more than a few fields grew past the
 * viewport and pushed its own Save button off the bottom of the screen with no
 * way to reach it. That is the entire reason these forms were built as a
 * permanent second column instead — the column was a workaround for a dialog
 * that could not hold a form.
 *
 * Traps focus while open, restores it to the trigger on close, locks body
 * scroll, and closes on overlay click or Escape.
 */
export function Dialog({
  open,
  onClose,
  title,
  description,
  children,
  confirm,
  dismiss,
  danger,
  closeLabel,
  size = 'md',
  className,
}: DialogProps): JSX.Element | null {
  const titleId = useId();
  const descId = useId();

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <FocusTrap
      focusTrapOptions={{
        // We handle escape ourselves above so the trap doesn't double-fire.
        escapeDeactivates: false,
        // Fallback so the trap can activate even when the dialog renders
        // before its first focusable child is mounted.
        fallbackFocus: '[role="dialog"]',
        // Restore focus to whatever was focused before we opened.
        returnFocusOnDeactivate: true,
        /* A MODAL EATS OUTSIDE CLICKS — except its own escaped children.
           `Combobox` renders its listbox on `document.body` so a scrolling
           container cannot clip it, which puts the options outside this trap
           even though they belong to a field inside it. With the default the
           trap calls `preventDefault` on that click and the option never
           commits: on the bulk "Set position" dialog the list opened, showed
           the designations, and picking one did nothing at all.
           Everything else outside stays blocked, which is what makes it a
           modal. `data-portal-surface` is the marker any portalled surface
           carries to say "I belong to whatever opened me" — the same attribute
           FiltersPopover's dismiss handler honours, so neither component has to
           know about `Combobox`. */
        allowOutsideClick: (e: MouseEvent | TouchEvent) =>
          e.target instanceof Element && e.target.closest('[data-portal-surface]') !== null,
      }}
    >
      <div
        // Bottom-anchored at EVERY width, not just on a phone. This is the
        // sister app's sheet, and centring it from `sm` up was the difference
        // that kept reading as a different component no matter what the
        // borders did — the anchor is the shape of the thing.
        // FULL-BLEED on a phone. The `px-4` here was inset-ing the sheet from
        // both edges, so it sat ~343px wide inside a 375px screen with two
        // stripes of dimmed page either side — a sheet that meets the bottom
        // edge but not the sides reads as a floating card that has slipped, not
        // as a sheet. From `sm` up the cap does the work instead.
        className="fixed inset-0 z-50 flex items-end justify-center sm:px-4"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descId : undefined}
      >
        <div
          className="absolute inset-0 bg-scrim/50"
          onClick={onClose}
          aria-hidden="true"
        />
        {/* `shadow-popover` — the one two-layer role in the ramp: a wide, very
            soft drop (`0 20px 50px -14px`) with a tight contact shadow under it
            (`0 2px 6px`). The contact layer is what stops a sheet looking like
            a decal: without it the panel floats with no edge where it meets the
            scrim.

            This was `shadow-elev-2xl`, a deprecated t-shirt alias, and the
            comment here described its old literal `0 20px 40px -4px` — a value
            that no longer exists anywhere. Naming the ROLE is the point: the
            role survives a re-cut of the ramp, a literal in a comment does not.

            Top corners use `rounded-modal` (18px), the largest step on the
            shape ramp — what marks this as the topmost layer. */}
        <div
          className={cn(
            // No cap below `sm`: the sheet IS the screen width there.
            'relative flex w-full flex-col bg-surface text-foreground shadow-popover',
            DIALOG_MAX[size],
            // Top corners only, at every width — it meets the bottom edge, so
            // there is no bottom corner to round.
            'rounded-t-modal',
            // The panel is bounded, so the body below can scroll rather than
            // the page growing under it.
            'max-h-[90vh]',
            'motion-safe:animate-dialog-in',
            className
          )}
        >
          {/* Grab handle — sheet affordance only. Decorative: the panel is
              dismissed by the close button, the overlay or Escape, all of
              which are reachable without it. */}
          <div
            className="mx-auto mt-2 h-1 w-10 shrink-0 rounded-full bg-border"
            aria-hidden="true"
          />
          {/* No rule under the header, and none over the footer — the sister
              app's sheet separates its sections with space alone. */}
          <div className="flex shrink-0 items-start justify-between gap-component px-card pb-card pt-card">
            <div>
              <h2 id={titleId} className="text-h2 text-foreground">
                {title}
              </h2>
              {description && (
                <p id={descId} className="mt-element text-copy-sm text-muted-foreground">
                  {description}
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label={closeLabel}
              className={cn(
                'state-layer -me-2 -mt-1 inline-flex h-11 w-11 shrink-0 items-center justify-center',
                'rounded-control text-muted-foreground transition-colors duration-fast ease-standard hover:text-foreground',
                focusRing
              )}
            >
              <X size={18} aria-hidden="true" />
            </button>
          </div>
          {/* The only part that scrolls. `min-h-0` is what actually lets it:
              a flex child will not shrink below its content without it, and the
              overflow would move to the panel — taking the header and footer
              out of view with it. */}
          {/* `pt-0`: the header's own bottom padding is already the gap. */}
          <div className="min-h-0 flex-1 overflow-y-auto px-card pb-card pt-0 text-copy-lg">
            {children}
          </div>
          {(confirm !== undefined || dismiss !== undefined || danger !== undefined) && (
            /* ONE GEOMETRY, DECIDED HERE.

               Stacked and full-width on a phone — this is a bottom sheet, and
               two auto-width buttons crowded into the corner of one is a pair
               of small targets at the far edge of a thumb's reach. From `sm`
               up a PAIR right-aligns, and a LONE confirm stays full width:
               that is the form shape, and it is the shape eight of the nine
               composers were already reaching for by hand.

               The dismiss is first in the DOM as well as on screen, so tab
               order and reading order agree with the eye. */
            <div className="flex shrink-0 flex-col gap-element px-card pb-card pt-element">
              {/* §3.11 — the obstacle, above the control it blocks, in the
                  same surface. Not a tooltip: the reader has to be able to read
                  why without hunting for it. Both actions can carry one; a
                  blocked Delete is the commoner of the two. */}
              {[confirm?.blockedBy, danger?.blockedBy]
                .filter((why): why is string => why !== undefined && why !== '')
                .map((why) => (
                  <p key={why} className="text-copy-sm text-muted-foreground">
                    {why}
                  </p>
                ))}
              <div
                className={cn(
                  'flex flex-col gap-element sm:flex-row sm:items-center sm:gap-component',
                  (dismiss !== undefined || danger !== undefined) && 'sm:justify-end'
                )}
              >
              {danger !== undefined && (
                /* Leading on a wide panel, pushed away from the primary by
                   `me-auto`; FIRST in the stack on a phone, which puts it
                   furthest from the thumb and Save nearest. DOM order and
                   visual order agree in both, so tab order does too. */
                <Button
                  type="button"
                  variant="ghost"
                  className="w-full text-danger hover:bg-danger-surface hover:text-danger sm:me-auto sm:w-auto"
                  onClick={danger.onClick}
                  disabled={
                    danger.disabled === true ||
                    danger.busy === true ||
                    (danger.blockedBy !== undefined && danger.blockedBy !== '')
                  }
                >
                  {danger.busy === true && danger.busyLabel !== undefined
                    ? danger.busyLabel
                    : danger.label}
                </Button>
              )}
              {dismiss !== undefined && (
                <Button
                  type="button"
                  variant="ghost"
                  className="w-full sm:w-auto"
                  onClick={dismiss.onClick}
                  disabled={dismiss.disabled === true}
                >
                  {dismiss.label}
                </Button>
              )}
              {confirm !== undefined && (
                <Button
                  type={confirm.form === undefined ? 'button' : 'submit'}
                  form={confirm.form}
                  variant={confirm.tone === 'danger' ? 'danger' : 'default'}
                  className={cn('w-full', dismiss !== undefined && 'sm:w-auto')}
                  onClick={confirm.onClick}
                  autoFocus={confirm.autoFocus === true}
                  disabled={
                    confirm.disabled === true ||
                    confirm.busy === true ||
                    (confirm.blockedBy !== undefined && confirm.blockedBy !== '')
                  }
                >
                  {confirm.busy === true && confirm.busyLabel !== undefined
                    ? confirm.busyLabel
                    : confirm.label}
                </Button>
              )}
              </div>
            </div>
          )}
        </div>
      </div>
    </FocusTrap>
  );
}
